import { get } from 'lodash-es';
import { DEAL_TYPES } from 'client/site-modules/shared/components/deal/deal-constants';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { ADVERTISED_PRICE_SOURCE } from 'site-modules/shared/constants/inventory/prices';

/**
 * Extracts and computes pricing display data from inventory prices and computed display info.
 *
 * @param {object} prices - Raw pricing data from the inventory record.
 * @param {number} [prices.tmv=0] - True Market Value of the vehicle.
 * @param {number} [prices.advertisedPrice=0] - The advertised price of the vehicle.
 * @param {string|undefined} [prices.advertisedPriceSource] - Source of the advertised price (e.g. 'DEALER_MSRP').
 * @param {number} [prices.dealerMsrp=0] - Dealer MSRP price.
 * @param {boolean} [prices.dealerMsrpStrikethroughIsCompliant=false] - Whether the MSRP strikethrough display is compliant.
 * @param {boolean} [prices.dealRatingIsCompliant=false] - Whether the deal rating is compliant.
 * @param {boolean} [prices.discountPriceIsCompliant=false] - Whether the discount price is compliant.
 * @param {number} [prices.docFee=0] - Documentation fee.
 * @param {boolean} [prices.docFeeIncluded=false] - Whether the doc fee is included in the advertised price.
 * @param {number} [prices.destinationFee=0] - Destination/delivery fee.
 * @param {boolean} [prices.edmundsSuggestedPriceIsCompliant=false] - Whether the Edmunds suggested price is compliant.
 * @param {number} [prices.edmundsSuggestedPrice=0] - Edmunds suggested price value.
 * @param {object} computedDisplayInfo - Computed display information for the inventory item.
 * @param {object} [computedDisplayInfo.priceValidation] - Price validation data.
 * @param {string} [computedDisplayInfo.priceValidation.dealType=''] - Deal type classification (e.g. 'GREAT', 'GOOD').
 * @param {number} [computedDisplayInfo.priceValidation.priceDiscount=0] - Discount amount relative to market.
 * @param {object} [computedDisplayInfo.transparentPricingCompliance] - Transparent pricing compliance flags.
 * @param {string} [inventoryType] - The inventory type (e.g. 'used', 'new', 'cpo').
 * @returns {{
 *   tmv: number,
 *   advertisedPrice: number,
 *   msrpPrice: number,
 *   advertisedPriceSource: string|undefined,
 *   isDealerMsrpAdvertisedPriceSource: boolean,
 *   priceStrikethrough: boolean,
 *   isDealTypeAvailable: boolean,
 *   dealType: string,
 *   isDisplayBelowAboveMarketCopy: boolean,
 *   savings: number,
 *   docFee: number,
 *   docFeeIncluded: boolean,
 *   destinationFee: number,
 *   hideDestinationFee: boolean,
 *   edmundsSuggestedPrice: number,
 * }} Computed pricing display object.
 */
export function getPrices(prices, computedDisplayInfo, inventoryType) {
  const advertisedPrice = get(prices, 'advertisedPrice', 0);
  const advertisedPriceSource = get(prices, 'advertisedPriceSource');
  const priceStrikethrough =
    get(prices, 'dealerMsrpStrikethroughIsCompliant', false) &&
    advertisedPriceSource === ADVERTISED_PRICE_SOURCE.DEALER_MSRP;
  const dealRatingIsCompliant = get(prices, 'dealRatingIsCompliant', false);
  const dealType = get(computedDisplayInfo, 'priceValidation.dealType', '');
  const isDealTypeAvailable = Boolean(
    inventoryType && isUsed(inventoryType) && advertisedPrice && dealRatingIsCompliant && DEAL_TYPES.includes(dealType)
  );

  const discountPriceIsCompliant = get(prices, 'discountPriceIsCompliant', false);
  const savings = get(computedDisplayInfo, 'priceValidation.priceDiscount', 0);
  const isDisplayBelowAboveMarketCopy = discountPriceIsCompliant && !!savings;

  const edmundsSuggestedPrice =
    (get(prices, 'edmundsSuggestedPriceIsCompliant', false) && get(prices, 'edmundsSuggestedPrice', 0)) || 0;

  return {
    tmv: get(prices, 'tmv', 0),
    advertisedPrice,
    msrpPrice: get(prices, 'dealerMsrp', 0),
    advertisedPriceSource,
    priceStrikethrough,
    isDealTypeAvailable,
    dealType,
    isDisplayBelowAboveMarketCopy,
    savings,
    docFee: get(prices, 'docFee', 0),
    docFeeIncluded: get(prices, 'docFeeIncluded', false),
    destinationFee: get(prices, 'destinationFee', 0),
    hideDestinationFee: !get(prices, 'destinationFeeIsCompliant', false),
    edmundsSuggestedPrice,
  };
}
